import type { PromptFilter, PromptStore, PromptTemplate, RenderedPrompt } from '../../ports/prompts'
import { BackendError } from '../../errors'
import { delay, uid } from './util'

/** Seed prompts, also used to bootstrap the server-side prompt store. */
export const PROMPT_SEED: PromptTemplate[] = [
  {
    id: 'p_handlungsaufforderungen',
    name: 'Handlungsaufforderungen identifizieren und formulieren',
    command: '/handlungsaufforderungen',
    version: 1,
    description:
      'Identifiziert Handlungsaufforderungen, formuliert sie im imperativischen Infinitiv und ergänzt Erläuterungen und Bedingungen.',
    body:
      'Identifiziere die Handlungsaufforderungen. Formuliere die Handlungsaufforderungen im imperativischen Infinitiv. Formuliere das Handlungsobjekt mit Artikel. Beispiel: "Die Befestigungsschrauben lösen." Bringe die Handlungsaufforderungen in die richtige Reihenfolge, falls nötig.\n' +
      'Falls eine Handlungsaufforderung eine Erläuterung enthält, formuliere diese in einem eigenen Satz. Du erkennst Erläuterungen an Formulierungen mit "um ... zu ...". Leite diesen Satz mit "Dadurch" ein. Platziere den Satz direkt hinter der Handlungsaufforderung. Erfinde keine Erläuterungen. Wenn die Handlungsaufforderungen zeitliche Vorgaben oder andere Bedingungen enthalten, stelle diese an den Anfang der Handlungsaufforderung. Beginne mit "Wenn", "Falls" oder "Sobald". Erfinde keine Vorgaben oder Bedingungen.\n\n' +
      'Abschnitt:\n{{auswahl}}',
    variables: ['auswahl'],
    tags: ['Dokumentation', 'Handlungsaufforderung'],
    source: 'local',
    host: 'word',
  },
  {
    id: 'p_funktionsbeschreibung',
    name: 'Funktionsbeschreibung aufräumen',
    command: '/funktionsbeschreibung',
    version: 1,
    description:
      'Strukturiert eine Funktionsbeschreibung nach Thema-Rhema mit Zwischenüberschriften; technische Daten als Tabelle, Handlungen ans Ende.',
    body:
      'Strukturiere den Inhalt nach Thema-Rhema (Durchlaufendes Thema, Lineare Progression oder Gespaltenes Rhema) in einem Fließtext. Wenn ein neues Thema beginnt, ergänze eine passende Zwischenüberschrift. Wenn der Inhalt technische Daten enthält, stelle diese ans Ende und stelle sie als Tabelle dar. Wenn der Inhalt Handlungen enthält, die von Personen durchgeführt werden, stelle diese ans Ende unter eine Überschrift "Handlungen".\n\n' +
      'Abschnitt:\n{{auswahl}}',
    variables: ['auswahl'],
    tags: ['Dokumentation', 'Funktionsbeschreibung'],
    source: 'local',
    host: 'word',
  },
  {
    id: 'p_einstellwerte',
    name: 'Einstellwerte sortieren',
    command: '/einstellwerte',
    version: 1,
    description:
      'Trennt technische Daten (Einstellwerte) von Handlungen und stellt Erläuterungen ohne Wiederholungen voran.',
    body:
      'Identifiziere Technische Daten. Stelle diese unter eine Überschrift Einstellwerte.\n' +
      'Identifiziere Handlungen. Formuliere sie im imperativischen Infinitiv und bringe sie in die richtige Reihenfolge.\n' +
      'Stelle Erläuterungen zu den Daten und Handlungen an den Anfang. Wiederhole dabei keine Informationen aus den Handlungen oder Daten.\n\n' +
      'Abschnitt:\n{{auswahl}}',
    variables: ['auswahl'],
    tags: ['Dokumentation', 'Einstellwerte'],
    source: 'local',
    host: 'word',
  },
  {
    id: 'p_sicherheitshinweise',
    name: 'Sicherheitshinweise identifizieren und formulieren',
    command: '/sicherheitshinweise',
    version: 1,
    description:
      'Erkennt sicherheitsbezogene Informationen und formuliert strukturierte Sicherheitshinweise (Überschrift, Risiko, Vermeidungsmaßnahmen).',
    body:
      'Identifiziere alle sicherheitsbezogenen Informationen in diesem Abschnitt. Du erkennst sie beispielsweise an folgenden Wörtern: Gefahr, Warnung, Vorsicht, Achtung, Risiko, Schaden, Beschädigung, sofort, unverzüglich, nie, niemals, nicht, kein\n' +
      'Strukturiere die Informationen je Thema nach folgendem Muster:\n' +
      'Gib eine Überschrift an. Verwende in der Überschrift ein Verb, z. B. indem du die zentrale Vermeidungsmaßnahme nennst. Beispiele: "Schutzausrüstung tragen", "Mit Gefahrstoffen umgehen"\n' +
      'Erkläre das Risiko. Halte dich dabei kurz, aber achte darauf, dass die Art und Quelle der Gefahr angegeben sind und die Folgen, die durch Nichtbeachten der Information entstehen können.\n' +
      'Liste die Vermeidungsmaßnahmen auf. Formuliere sie als Handlungsaufforderungen im imperativischen Infinitiv.\n' +
      'Beispiel für die Umsetzung:\n' +
      '"Sicher mit Betriebsstoffen umgehen\n' +
      'Im Bereich der Vorstraße werden Betriebs- und Hilfsstoffe (z.B. Öle, Fette, Reiniger) eingesetzt.\n' +
      '- Die Betriebsanweisungen der eingesetzten Stoffe beachten.\n' +
      '- Betriebs- und Hilfsstoffe gemäß QUAA-AMEH-UM-27-01 entsorgen.\n' +
      '- Die Öl- und Fettversorgung gemäß QUAA-WWW-VS-11-02 auf Leckagen prüfen.\n' +
      '- Bei Ausbau der Walzen das Gerüstfenster und die Lager auf Öl- und Fettaustritt prüfen.\n' +
      '- Ausgetretene Fette und Öle aufnehmen und als Abfall entsorgen.\n' +
      '- Bei übermäßigen Leckagen den SM WWW informieren. Der SM WWW beauftragt die IH-M mit der Behebung."\n' +
      'Wenn Informationen fehlen: setze einen Platzhalter in eckigen Klammern ein, der benennt, welche Information fehlt. Beispiel: "[Folgen der Gefahr nennen]"\n\n' +
      'Abschnitt:\n{{auswahl}}',
    variables: ['auswahl'],
    tags: ['Dokumentation', 'Sicherheit'],
    source: 'local',
    host: 'word',
  },
  {
    id: 'p_informationsarten',
    name: 'Übrige Informationsarten auflisten',
    command: '/informationsarten',
    version: 1,
    description:
      'Klassifiziert nicht zur Informationsart passende Inhalte (Sicherheit, Aufbau, Funktion+Prozess, Daten) und stellt sie ans Ende.',
    body:
      'Wenn der Inhalt andere Informationsarten als Handlungsaufforderungen enthält, klassifiziere nach folgenden Informationsarten:\n' +
      'SICHERHEIT - du erkennst diese Informationsart an folgenden, typischen Wörtern: Gefahr, Warnung, Vorsicht, Achtung, Risiko, Schaden, Beschädigung, sofort, unverzüglich, nie, niemals, nicht, kein\n' +
      'AUFBAU - du erkennst diese Informationsart an folgenden, typischen Verben: dient dem/der … / dient zum/zur…, besteht aus, angeordnet, angebracht; Ausnahme: wenn das Verb "einbauen" mit einem nominalisierten anderen Verb zusammen auftritt, nicht als AUFBAU klassifizieren\n' +
      'FUNKTION+PROZESS - du erkennst diese Informationsart an folgenden, typischen Wörter: übergibt, steuert, bewegt, wird/werden … verwendet, zum/zur …, Prozess, anzeigen/angezeigt, produzieren; die Verben können auch in ihrer Passivform oder als Nominalisierung vorkommen\n' +
      'DATEN - du erkennst diese Informationsart an folgenden, typischen Inhalten: Daten und Einheiten; Wörter wie betragen, größer als, kleiner als, Differenz; die Verben können auch in ihrer Passivform oder als Nominalisierung vorkommen; Ausnahme: wenn ein anderes Verb als "haben" oder "sein" im selben Satz vorkommt, nicht als DATEN klassifizieren\n' +
      'Stelle diesen Inhalt nach Informationsarten sortiert und mit zur Informationsart passenden Überschriften versehen ans Ende des Textes.\n\n' +
      'Abschnitt:\n{{auswahl}}',
    variables: ['auswahl'],
    tags: ['Dokumentation', 'Klassifikation'],
    source: 'local',
    host: 'word',
  },
]

export class MockPromptStore implements PromptStore {
  private prompts: PromptTemplate[] = PROMPT_SEED.map((p) => ({ ...p }))

  constructor(private readonly latency: number) {}

  async list(filter?: PromptFilter): Promise<PromptTemplate[]> {
    await delay(this.latency)
    let out = [...this.prompts]
    if (filter?.source) out = out.filter((p) => p.source === filter.source)
    if (filter?.host) out = out.filter((p) => p.host === filter.host || p.host === 'all')
    if (filter?.tags?.length) {
      out = out.filter((p) => filter.tags!.some((t) => p.tags?.includes(t)))
    }
    if (filter?.search) {
      const q = filter.search.toLowerCase()
      out = out.filter(
        (p) => p.name.toLowerCase().includes(q) || (p.command?.toLowerCase().includes(q) ?? false),
      )
    }
    return out
  }

  async get(id: string): Promise<PromptTemplate> {
    await delay(this.latency)
    const p = this.prompts.find((x) => x.id === id)
    if (!p) throw new BackendError({ code: 'not_found', message: `Prompt ${id} not found` })
    return p
  }

  async render(id: string, vars: Record<string, string>): Promise<RenderedPrompt> {
    const p = await this.get(id)
    const text = p.body.replace(/\{\{(\w+)\}\}/g, (_, key: string) => vars[key] ?? `{{${key}}}`)
    return { id: p.id, version: p.version, text }
  }

  async create(
    input: Omit<PromptTemplate, 'id' | 'version' | 'updatedAt'>,
  ): Promise<PromptTemplate> {
    await delay(this.latency)
    const p: PromptTemplate = {
      ...input,
      id: uid('p'),
      version: 1,
      updatedAt: new Date().toISOString(),
    }
    this.prompts.push(p)
    return p
  }

  async update(id: string, patch: Partial<PromptTemplate>): Promise<PromptTemplate> {
    const p = await this.get(id)
    Object.assign(p, patch, { version: p.version + 1, updatedAt: new Date().toISOString() })
    return p
  }

  async delete(id: string): Promise<void> {
    await delay(this.latency)
    this.prompts = this.prompts.filter((x) => x.id !== id)
  }
}
